import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';

/**
 * Composant pour l'effet de frappe progressive (typewriter)
 * @param {string} text - Le texte à afficher progressivement
 * @param {number} speed - Vitesse de frappe en millisecondes par caractère (défaut: 50)
 * @param {number} delay - Délai avant le début de la frappe en millisecondes (défaut: 0)
 * @param {function} onComplete - Callback appelé quand la frappe est terminée
 * @param {React.Component} children - Composant à utiliser pour rendre le texte (défaut: simple texte)
 * @param {string} cursorColor - Couleur du curseur clignotant
 */
const TypewriterText = ({ 
  text, 
  speed = 50, 
  delay = 0, 
  onComplete = null,
  children,
  cursorColor = '#FF6B6B'
}) => {
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  
  // Utiliser des refs pour éviter les problèmes de closure et cleanup
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const currentTextRef = useRef('');

  // Mettre à jour la ref du callback pour éviter les re-déclenchements
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Fonction de cleanup memoized
  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Fonction pour terminer l'animation
  const completeAnimation = useCallback(() => {
    setIsTyping(false);
    setIsComplete(true);
    cleanup();
    // Utiliser un petit délai pour éviter les appels multiples
    setTimeout(() => {
      if (onCompleteRef.current && typeof onCompleteRef.current === 'function') {
        onCompleteRef.current();
      }
    }, 50);
  }, [cleanup]);

  useEffect(() => {
    // Si pas de texte ou si l'animation est déjà terminée pour ce texte, ne rien faire
    if (!text || currentTextRef.current === text) return;
    
    // Nettoyer toute animation en cours
    cleanup();
    
    // Réinitialiser les états
    setDisplayText('');
    setIsTyping(true);
    setIsComplete(false);
    currentTextRef.current = text;

    // Démarrer l'animation avec délai
    timeoutRef.current = setTimeout(() => {
      let currentIndex = 0;
      
      intervalRef.current = setInterval(() => {
        // Vérifier si l'animation n'a pas été interrompue
        if (!timeoutRef.current && !intervalRef.current) return;
        
        if (currentIndex <= text.length) {
          const newText = text.slice(0, currentIndex);
          setDisplayText(newText);
          currentIndex++;
        } else {
          // Animation terminée
          completeAnimation();
        }
      }, speed);
    }, delay);

    // Cleanup lors du démontage ou changement de props
    return cleanup;
  }, [text, speed, delay, cleanup, completeAnimation]);

  // Cleanup lors du démontage
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      {children ? (
        React.cloneElement(children, { children: displayText })
      ) : (
        <span>{displayText}</span>
      )}
      {isTyping && !isComplete && (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: '3px',
            height: '1.2em',
            backgroundColor: cursorColor,
            marginLeft: '2px',
            verticalAlign: 'text-bottom',
            animation: 'typewriterBlink 1s infinite',
            '@keyframes typewriterBlink': {
              '0%, 50%': { opacity: 1 },
              '51%, 100%': { opacity: 0 }
            }
          }}
        />
      )}
    </Box>
  );
};

export default TypewriterText; 